import type { Composer, I18n } from 'vue-i18n';
import { createI18n } from 'vue-i18n';
import { instance as api } from '@/helpers/api';

let i18n: I18n | null = null;

const supportedLocales = ['de', 'en'];
const dayJsLocaleMap = {
    de: 'de-ch',
    en: 'en-gb'
};

/**
 * Loads the locale strings from locales/[locale].json. Returns a promise
 * which resolves the key/value strings for that locale.
 * @param {string} locale
 * @returns {Promise<string>}
 */
export async function setupLocale(locale: string) {
    if (supportedLocales.indexOf(locale) < 0) {
        locale = 'de'; // fallback locale
    }

    // load correct locale strings, then load customer specific strings, if any, then merge and return the result:
    const res = await api().get(`locales/${locale}.json`);
    return res.data || {};
}

/**
 * Creates a singleton instance of the i18n VueJS plugin
 * Use this function to get an instance of the I18n plugin
 * to be injected into your vue application.
 */
export function setupI18n(locale: string, messages: { [key: string]: any }): I18n {
    if (i18n) {
        console.error('i18n already initialized! Make sure this function is only called once.');
    }
    i18n = createI18n({
        legacy: false,
        warnHtmlInMessage: false,
        warnHtmlMessage: false,
        globalInjection: true,
        locale: locale,
        fallbackLocale: 'de',
        availableLocales: ['de'],
        messages: { [locale]: messages }
    });
    return i18n;
}

/**
 * Get the singleton instance of the global Composer
 * instance, to be used for programmatic translations,
 * e.g. 'getI18n().t('key')'.
 */
export function getI18n(): Composer<{}, {}, {}, string, never, string> {
    if (!i18n) {
        console.error('i18n not initialized! Use setupI18n() first.');
        console.trace();
        throw new Error('i18n not initialized! Use setupI18n() first.');
    }
    return i18n.global as Composer<{}, {}, {}, string, never, string>;
}
